import { useEffect, useState } from "react";
import styles from "./index.module.scss";
import classNames from "classnames/bind";
import useMatterJS from "./useMatterJS";
import { Fruit, getRandomFruitFeature } from "./object/Fruit";
import GameOverModal from "./gameOverModal";
import InstagramModal from "./instagramModal";
import SpecialModal from "./specialModal";
import Intro from "./intro";
import Header from "./header";
import { db } from "../../firebase";
import { collection, addDoc } from "firebase/firestore";
import { getRenderHeight, getRenderWidth } from "./object/Size";

const cx = classNames.bind(styles);

const SuikaGame = () => {
  const [bestScore, setBestScore] = useState(0);
  const [score, setScore] = useState(0);
  const [nextItem, setNextItem] = useState<Fruit>(
    getRandomFruitFeature()?.label as Fruit
  );
  const [isStart, setIsStart] = useState<boolean>(false);
  const [isGameOver, setIsGameOver] = useState<boolean>(false);
  const [isGameOverModalVisible, setIsGameOverModalVisible] =
    useState<boolean>(false);
  const [isInstagramModalVisible, setIsInstagramModalVisible] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const [hasInstagramModalShown, setHasInstagramModalShown] = useState(false);
  const [playerName, setPlayerName] = useState("");
  const [isSpecialModalVisible, setIsSpecialModalVisible] = useState(false);
  const [isNewGame, setIsNewGame] = useState(false);

  const { clear } = useMatterJS({
    score,
    setScore,
    nextItem,
    setNextItem,
    isGameOver,
    setIsGameOver,
    setIsInstagramModalVisible,
    setIsPaused,
    setHasInstagramModalShown,
    openSpecialModal: () => setIsSpecialModalVisible(true),
  });

  useEffect(() => {
    const bestScore = localStorage.getItem("bestScore");
    if (bestScore) setBestScore(Number(bestScore));
  }, [isGameOver]);

  useEffect(() => {
    if (isGameOver) {
      const bestScore = localStorage.getItem("bestScore") || "0";
      if (score > Number(bestScore)) {
        localStorage.setItem("bestScore", score.toString());
      }
      setIsGameOverModalVisible(true);
      // isNewGame은 그대로 유지 (게임 시작/재시작 시 true로 설정됨)
    }
  }, [isGameOver, score]);

  useEffect(() => {
    const updateScale = () => {
      const root = document.documentElement;
      const windowHeight = window.innerHeight;
      const windowWidth = window.innerWidth;
      const gameHeight = getRenderHeight();
      const gameWidth = getRenderWidth();

      // 헤더 높이를 동적으로 계산 (실제 DOM 요소에서)
      const headerElement = document.querySelector('[class*="headerArea"]');
      const headerHeight = headerElement ? headerElement.getBoundingClientRect().height : 64;

      // 전체 게임 영역 높이 (헤더 + 게임 영역 + 여백)
      const totalGameHeight = gameHeight + headerHeight + 40; // 게임 영역 + 헤더 + 여백
      const totalGameWidth = gameWidth + 40; // 게임 영역 + 좌우 여백

      // 화면에 맞게 스케일 계산
      const scaleX = windowWidth / totalGameWidth;
      const scaleY = windowHeight / totalGameHeight;
      const scale = Math.min(scaleX, scaleY, 1); // 전체가 보이도록 스케일 계산, 최대 1로 제한

      // 아이폰 SE 같은 작은 화면에서는 더 보수적으로 스케일링
      const isSmallScreen = windowHeight < 600 || windowWidth < 400;
      const finalScale = isSmallScreen ? Math.min(scale, 0.8) : scale;

      root.style.setProperty("--game-scale", finalScale.toString());
    };

    updateScale();
    window.addEventListener("resize", updateScale);
    return () => window.removeEventListener("resize", updateScale);
  }, []);

  const handleTryAgain = () => {
    setScore(0);
    setNextItem(getRandomFruitFeature()?.label as Fruit);
    setIsGameOver(false);
    setIsGameOverModalVisible(false);
    setIsNewGame(true); // 재시작도 새 게임으로 간주
    clear();
  };

  const handleGameStart = () => {
    setIsStart(true);
    setIsNewGame(true); // 새 게임 시작
  };

  const handleHomeClick = () => {
    setIsStart(false);
    setScore(0);
    setNextItem(getRandomFruitFeature()?.label as Fruit);
    setIsGameOver(false);
    clear();
  };

  const handleInstagramSubmit = async (id: string) => {
    try {
      await addDoc(collection(db, "instagram"), {
        id: id,
        score: score,
        createdAt: new Date(),
      });
      console.log("인스타그램 ID와 점수가 성공적으로 저장되었습니다!");
      setIsInstagramModalVisible(false);
    } catch (error) {
      console.error("인스타그램 ID 저장 중 오류 발생:", error);
      alert("인스타그램 ID 저장에 실패했습니다. 다시 시도해주세요.");
    }
  };

  const handleCloseGameOverModal = () => {
    setIsGameOverModalVisible(false);
    setIsNewGame(false); // 모달 닫을 때 광고 상태 리셋
  };

  const handleCanvasClick = () => {
    if (isGameOver && !isGameOverModalVisible) {
      setIsGameOverModalVisible(true);
    }
  };

  const handleCanvasTouch = () => {
    if (isGameOver && !isGameOverModalVisible) {
      setIsGameOverModalVisible(true);
    }
  };

  return (
    <div
      className={cx("gameArea")}
      onClick={handleCanvasClick}
      onTouchStart={handleCanvasTouch}
    >
      <div
        className={cx("gameWrap")}
        style={{ visibility: isStart ? "visible" : "hidden" }}
      >
        <div className={cx("canvasArea")}>
          <Header
            bestScore={bestScore}
            score={score}
            nextItem={nextItem}
            onHomeClick={handleHomeClick}
          />
          <div id={"canvasWrap"} className={cx("canvasWrap")} />
        </div>
      </div>

      <Intro isVisible={!isStart} handleGameStart={handleGameStart} />
      <GameOverModal
        isVisible={isGameOverModalVisible}
        onClick={handleTryAgain}
        score={score}
        isInstagramModalVisible={isInstagramModalVisible}
        setIsInstagramModalVisible={setIsInstagramModalVisible}
        playerName={playerName}
        setPlayerName={setPlayerName}
        bestScore={bestScore}
        onClose={handleCloseGameOverModal}
        isNewGame={isNewGame}
      />

      {isInstagramModalVisible && (
        <InstagramModal isVisible={true} onSubmit={handleInstagramSubmit} />
      )}

      {isSpecialModalVisible && (
        <SpecialModal
          isVisible={true}
          onClose={() => setIsSpecialModalVisible(false)}
        />
      )}
    </div>
  );
};

export default SuikaGame;
